import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import XIcon from './icons/XIcon';
import CropIcon from './icons/CropIcon';
import RefreshCwIcon from './icons/RefreshCwIcon';

interface ImageCropperProps {
  imageUrl: string;
  onCrop: (croppedImageBase64: string) => void;
  onCancel: () => void;
}

type Point = { x: number; y: number };
type Rect = { x: number; y: number; width: number; height: number };

const ImageCropper: React.FC<ImageCropperProps> = ({ imageUrl, onCrop, onCancel }) => {
  const imageContainerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  
  const [startPoint, setStartPoint] = useState<Point | null>(null);
  const [cropRect, setCropRect] = useState<Rect | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);

  const handleClearCrop = () => {
    setCropRect(null);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    const container = imageContainerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    setStartPoint({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    setIsDrawing(true);
    setCropRect(null); // Reset rect on new draw
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDrawing || !startPoint) return;
    const container = imageContainerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const currentX = e.clientX - rect.left;
    const currentY = e.clientY - rect.top;

    const newRect: Rect = {
      x: Math.min(startPoint.x, currentX),
      y: Math.min(startPoint.y, currentY),
      width: Math.abs(currentX - startPoint.x),
      height: Math.abs(currentY - startPoint.y),
    };
    setCropRect(newRect);
  };

  const handleMouseUp = () => {
    setIsDrawing(false);
  };

  const handleConfirmCrop = () => {
    const image = imageRef.current;
    const container = imageContainerRef.current;
    if (!cropRect || !image || !container || cropRect.width === 0 || cropRect.height === 0) return;

    // 1. Get displayed image dimensions based on `object-contain`
    const containerRatio = container.clientWidth / container.clientHeight;
    const imageRatio = image.naturalWidth / image.naturalHeight;
    let displayedWidth, displayedHeight, displayedX, displayedY;

    if (imageRatio > containerRatio) { // letterboxed (image is wider)
        displayedWidth = container.clientWidth;
        displayedHeight = displayedWidth / imageRatio;
        displayedX = 0;
        displayedY = (container.clientHeight - displayedHeight) / 2;
    } else { // pillarboxed (image is taller)
        displayedHeight = container.clientHeight;
        displayedWidth = displayedHeight * imageRatio;
        displayedY = 0;
        displayedX = (container.clientWidth - displayedWidth) / 2;
    }

    // 2. Calculate scaling factor
    const scaleX = image.naturalWidth / displayedWidth;
    const scaleY = image.naturalHeight / displayedHeight;
    
    // 3. Convert cropRect (relative to container) to source image coordinates
    // First, make the crop rect relative to the displayed image
    const cropX_rel = cropRect.x - displayedX;
    const cropY_rel = cropRect.y - displayedY;

    // Clamp coordinates to be within the displayed image boundaries before scaling
    const clampedCropX = Math.max(0, cropX_rel);
    const clampedCropY = Math.max(0, cropY_rel);
    const clampedCropWidth = Math.min(cropRect.width - (clampedCropX - cropX_rel), displayedWidth - clampedCropX);
    const clampedCropHeight = Math.min(cropRect.height - (clampedCropY - cropY_rel), displayedHeight - clampedCropY);

    if (clampedCropWidth <= 0 || clampedCropHeight <= 0) return;

    // Now, scale the clamped coordinates
    const sx = clampedCropX * scaleX;
    const sy = clampedCropY * scaleY;
    const sWidth = clampedCropWidth * scaleX;
    const sHeight = clampedCropHeight * scaleY;

    // 4. Create new canvas and draw cropped part
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = sWidth;
    tempCanvas.height = sHeight;
    const tempCtx = tempCanvas.getContext('2d');
    if (!tempCtx) return;

    tempCtx.drawImage(
        image,
        sx, sy, sWidth, sHeight, // source rect
        0, 0, sWidth, sHeight   // destination rect
    );

    // 5. Get base64 and call onCrop
    const croppedBase64 = tempCanvas.toDataURL('image/png');
    onCrop(croppedBase64);
  };
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black/80 backdrop-blur-md p-2 sm:p-4 md:p-6"
    >
      <motion.div
        initial={{ scale: 0.95, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.95, y: 20, opacity: 0 }}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
        className="relative w-full h-full max-w-4xl max-h-[90vh] sm:max-h-[92vh] md:max-h-[95vh] bg-zinc-950/80 backdrop-blur-xl rounded-xl md:rounded-2xl border border-white/10 shadow-2xl shadow-black/40 flex flex-col overflow-hidden"
      >
        <header className="flex-shrink-0 p-2 sm:p-3 md:p-4 flex justify-between items-center border-b border-white/10">
          <h2 className="text-sm sm:text-base md:text-lg font-bold text-zinc-100 flex items-center gap-2 sm:gap-3">
            <CropIcon className="w-4 h-4 sm:w-5 sm:h-5 text-teal-400" />
            Select Full Mouth Area
          </h2>
           <button onClick={onCancel} className="p-1.5 sm:p-2 text-zinc-400 rounded-full hover:bg-white/10 transition-colors"><XIcon className="w-4 h-4 sm:w-5 sm:h-5" /></button>
        </header>

        <main 
            ref={imageContainerRef}
            className="flex-1 min-h-0 p-2 sm:p-3 md:p-4 flex items-center justify-center relative select-none"
        >
            <div 
                className="absolute inset-0 z-10"
                style={{ cursor: 'crosshair' }}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
            />

            <img
                ref={imageRef}
                src={imageUrl}
                className="max-w-full max-h-full object-contain pointer-events-none"
                crossOrigin="anonymous" // Important for canvas operations
            />

            {/* Dimming and selection rectangle overlay */}
            {cropRect && (
                <div className="absolute inset-0 pointer-events-none">
                    {/* Dimming overlays */}
                    <div className="absolute left-0 top-0 right-0 bg-black/60" style={{ height: cropRect.y }} />
                    <div className="absolute left-0 bottom-0 right-0 bg-black/60" style={{ top: cropRect.y + cropRect.height }} />
                    <div className="absolute left-0 bg-black/60" style={{ top: cropRect.y, height: cropRect.height, width: cropRect.x }} />
                    <div className="absolute right-0 bg-black/60" style={{ top: cropRect.y, height: cropRect.height, left: cropRect.x + cropRect.width }} />

                    {/* Rectangle border */}
                    <div 
                        className="absolute border-2 border-dashed border-teal-400"
                        style={{
                            left: cropRect.x,
                            top: cropRect.y,
                            width: cropRect.width,
                            height: cropRect.height,
                            boxShadow: '0 0 15px rgba(20, 184, 166, 0.5)'
                        }}
                    />
                </div>
            )}
             {!cropRect && !isDrawing && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <p className="px-3 sm:px-4 py-1.5 sm:py-2 bg-black/50 text-white rounded-lg text-xs sm:text-sm font-medium animate-pulse">Draw a rectangle around the mouth</p>
                </div>
            )}
        </main>
        
        <footer className="flex-shrink-0 p-2 sm:p-3 md:p-4 border-t border-white/10 flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-2 sm:gap-3 md:gap-4">
             <button
                onClick={handleClearCrop}
                disabled={!cropRect}
                className="px-3 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm font-semibold text-zinc-300 bg-white/5 hover:bg-white/10 rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
             >
                <RefreshCwIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4"/>
                <span className="hidden sm:inline">Clear Selection</span>
                <span className="sm:hidden">Clear</span>
            </button>
            <div className="flex items-center gap-2 sm:gap-3 md:gap-4">
              <button onClick={onCancel} className="px-4 sm:px-5 py-2 sm:py-2.5 text-xs sm:text-sm font-semibold text-zinc-300 bg-white/10 hover:bg-white/20 rounded-lg transition-colors">Cancel</button>
              <motion.button
                  onClick={handleConfirmCrop}
                  disabled={!cropRect || cropRect.width === 0}
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  className="px-4 sm:px-5 py-2 sm:py-2.5 text-xs sm:text-sm font-semibold bg-gradient-to-br from-teal-400 to-teal-600 text-white rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:from-zinc-600 disabled:to-zinc-700"
              >
                  <span className="hidden sm:inline">Confirm & Generate</span>
                  <span className="sm:hidden">Confirm</span>
              </motion.button>
            </div>
        </footer>

      </motion.div>
    </motion.div>
  );
};

export default ImageCropper;
